import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { GithubAuth, Repository } from '../types';
import { Octokit } from '@octokit/rest';

interface GithubContextProps {
  auth: GithubAuth | null;
  loading: boolean;
  repositories: Repository[];
  selectedRepo: Repository | null;
  login: (code: string) => Promise<void>;
  logout: () => void;
  fetchRepositories: () => Promise<void>;
  selectRepository: (repo: Repository) => void;
}

export const GithubContext = createContext<GithubContextProps>({
  auth: null,
  loading: false,
  repositories: [],
  selectedRepo: null,
  login: async () => {},
  logout: () => {},
  fetchRepositories: async () => {},
  selectRepository: () => {},
});

interface GithubProviderProps {
  children: ReactNode;
}

// Client ID should be stored in environment variables in a real app
const VITE_GITHUB_CLIENT_ID = import.meta.env.VITE_GITHUB_CLIENT_ID || '';

export const GithubProvider: React.FC<GithubProviderProps> = ({ children }) => {
  const [auth, setAuth] = useState<GithubAuth | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<Repository | null>(null);
  
  // Load auth from localStorage on mount
  useEffect(() => {
    const storedAuth = localStorage.getItem('github_auth');
    if (storedAuth) {
      try {
        setAuth(JSON.parse(storedAuth));
      } catch (error) {
        console.error('Failed to parse stored auth:', error);
        localStorage.removeItem('github_auth');
      }
    }
    setLoading(false);
  }, []);

  const login = async (code: string) => {
    setLoading(true);
    try {
      // In a real app, you would exchange the code for a token via a secure backend
      // For this demo, we'll assume we got a token 
      // In Cloudflare Pages, you would have an API endpoint to handle token exchange
      
      // Mock auth response for demo
      const mockResponse = {
        token: `mock_token_${code}`,
        user: {
          login: 'demo_user',
          id: 123456,
          avatar_url: 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
          name: 'Demo User',
          bio: 'This is a demo user for GitHub HTML Tool',
        },
      };
      
      setAuth(mockResponse);
      localStorage.setItem('github_auth', JSON.stringify(mockResponse));
      
      // Fetch repositories after login
      await fetchRepositories();
    } catch (error) {
      console.error('Login failed:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const logout = () => {
    setAuth(null);
    setRepositories([]);
    setSelectedRepo(null);
    localStorage.removeItem('github_auth');
  };
  
  const fetchRepositories = async () => {
    setLoading(true);
    try {
      if (!auth?.token) {
        throw new Error('Not authenticated');
      }
      
      // In a real app, you would use the token to call the GitHub API
      // For this demo, we'll mock the response
      
      // Mock repositories
      const mockRepos: Repository[] = [
        {
          id: 1,
          name: 'demo-repo-1',
          full_name: 'demo_user/demo-repo-1',
          owner: {
            login: 'demo_user',
            avatar_url: 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
          },
          html_url: 'https://github.com/demo_user/demo-repo-1',
          description: 'A demo repository for testing GitHub HTML Tool',
          default_branch: 'main',
        },
        {
          id: 2,
          name: 'demo-repo-2',
          full_name: 'demo_user/demo-repo-2',
          owner: {
            login: 'demo_user',
            avatar_url: 'https://github.githubassets.com/images/modules/logos_page/GitHub-Mark.png',
          },
          html_url: 'https://github.com/demo_user/demo-repo-2',
          description: 'Another demo repository for testing GitHub HTML Tool',
          default_branch: 'main',
        },
      ];
      
      setRepositories(mockRepos);
    } catch (error) {
      console.error('Failed to fetch repositories:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const selectRepository = (repo: Repository) => {
    setSelectedRepo(repo);
  };
  
  return (
    <GithubContext.Provider 
      value={{ 
        auth, 
        loading, 
        repositories, 
        selectedRepo,
        login, 
        logout, 
        fetchRepositories,
        selectRepository,
      }}
    >
      {children}
    </GithubContext.Provider>
  );
}; 